/**
 * spinnerFonts.ts — vibe-designer 1.556.0
 *
 * Font registry for the Pi status-spinner MESSAGE (the "thinking" phrase). The
 * operator can pick a themed font: a Matrix-style terminal mono, a Japanese
 * (Noto Sans JP) face, or several English variants. Default keeps the current
 * Inter/system look so the spinner is unchanged until a font is chosen.
 *
 * 1.556.0 — two fonts gained real behaviour beyond styling:
 *   • `matrix` (scramble:true) drives a katakana → English decode animation
 *     (MatrixDecodeText.tsx). Its family falls back to 'Noto Sans JP' so the
 *     half-width katakana rendered mid-scramble have glyphs (Share Tech Mono
 *     is latin-only).
 *   • `kanji` (japanese:true) renders REAL Japanese message text with an
 *     English subtitle (spinnerJapanesePhrases.ts) — no longer English styled
 *     in a Japanese face.
 *
 * The @font-face declarations (bundled woff2, all OFL) live in
 * @/assets/fonts/spinnerFonts.css, imported here so any consumer of the
 * registry pulls the faces in. The ids match SPINNER_FONTS in chatPrefsStore.ts
 * (a spinnerFonts test asserts they agree so the store's validation set and
 * this registry can't drift).
 */
import '@/assets/fonts/spinnerFonts.css';
import type { SpinnerFontId } from '@/stores/chatPrefsStore';

export interface SpinnerFont {
  id: SpinnerFontId;
  /** Label for the settings select. */
  name: string;
  /** CSS font-family stack. */
  family: string;
  /** Matrix theme flips the message to green terminal styling (is-matrix). */
  matrix?: boolean;
  /** Matrix decode effect: message scrambles through katakana then resolves. */
  scramble?: boolean;
  /** Japanese mode: render a real Japanese phrase + English subtitle. */
  japanese?: boolean;
}

export const SPINNER_FONTS: SpinnerFont[] = [
  { id: 'default', name: 'Default', family: "'Inter', system-ui, sans-serif" },
  {
    id: 'matrix',
    name: 'Matrix (decode)',
    // Share Tech Mono for the resolved latin; Noto Sans JP supplies the
    // half-width katakana rendered while the phrase is still scrambling.
    family: "'Share Tech Mono', 'Noto Sans JP', ui-monospace, monospace",
    matrix: true,
    scramble: true,
  },
  {
    id: 'kanji',
    name: 'Kanji (日本語)',
    family: "'Noto Sans JP', system-ui, sans-serif",
    japanese: true,
  },
  { id: 'inter', name: 'Inter', family: "'Inter', system-ui, sans-serif" },
  { id: 'mono', name: 'JetBrains Mono', family: "'JetBrains Mono', ui-monospace, monospace" },
  { id: 'lora', name: 'Lora', family: "'Lora', Georgia, serif" },
  { id: 'space', name: 'Space Grotesk', family: "'Space Grotesk', system-ui, sans-serif" },
  { id: 'playfair', name: 'Playfair Display', family: "'Playfair Display', Georgia, serif" },
];

export const SPINNER_FONT_IDS: SpinnerFontId[] = SPINNER_FONTS.map((f) => f.id);

/** Look up a font by id, falling back to the default (index 0) for unknown ids. */
export function resolveSpinnerFont(id: string): SpinnerFont {
  return SPINNER_FONTS.find((f) => f.id === id) ?? SPINNER_FONTS[0];
}
